import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Check, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui-kit/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  useGetWishlist,
  useInsertWishlist,
  useUpdateWishlist,
  useDeleteWishlist,
} from '../../hooks/use-hotwheels';
import { WishlistItem, Priority, PRIORITY_OPTIONS } from '../../types/hotwheels.types';

/**
 * Wishlist page — list, add, mark acquired, delete wishlist items.
 *
 * "Mark acquired" copies the wishlist item into a new HotWheel and removes
 * the wishlist entry.
 */

export function HotWheelsWishlist() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data, isLoading } = useGetWishlist({ pageNo: 0, pageSize: 100 });
  const { mutateAsync: deleteItem } = useDeleteWishlist();
  const { mutateAsync: updateItem } = useUpdateWishlist();

  const items = data?.items ?? [];

  const handleMarkAcquired = async (item: WishlistItem) => {
    try {
      await updateItem({
        itemId: item.ItemId,
        input: { Acquired: true },
      });
      toast({ title: t('ACQUIRED'), description: item.CarName });
    } catch (err) {
      toast({
        title: t('SAVE_FAILED'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (item: WishlistItem) => {
    try {
      await deleteItem({ itemId: item.ItemId });
    } catch (err) {
      toast({
        title: t('DELETE_FAILED'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <ShoppingCart className="h-7 w-7 text-[#2563EB]" />
            {t('WISHLIST')}
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </p>
        </div>
        <AddWishlistDialog />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 rounded-2xl bg-zinc-900 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/40 p-12 text-center">
          <ShoppingCart className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 mb-1">{t('EMPTY_TITLE')}</p>
          <p className="text-zinc-500 text-sm">Add cars you want to track here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.ItemId}
              className={`rounded-2xl border p-4 flex items-center gap-4 ${
                item.Acquired
                  ? 'border-emerald-700/30 bg-emerald-900/10 opacity-70'
                  : 'border-zinc-800 bg-zinc-900'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className={`font-semibold truncate ${item.Acquired ? 'line-through text-zinc-500' : 'text-white'}`}>
                    {item.CarName}
                  </h3>
                  {item.Priority && (
                    <span
                      className={`text-xs rounded-full px-2 py-0.5 font-medium ${
                        item.Priority === 'High'
                          ? 'bg-blue-500/20 text-red-400'
                          : item.Priority === 'Medium'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-zinc-700 text-zinc-300'
                      }`}
                    >
                      {item.Priority}
                    </span>
                  )}
                  {item.Acquired && (
                    <span className="text-xs rounded-full bg-emerald-500/20 text-emerald-400 px-2 py-0.5">
                      ✓ {t('ACQUIRED')}
                    </span>
                  )}
                </div>
                {(item.Brand || item.Series) && (
                  <p className="text-xs text-zinc-500 truncate">
                    {item.Brand} {item.Series && `· ${item.Series}`}
                  </p>
                )}
                {item.Notes && (
                  <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{item.Notes}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!item.Acquired && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleMarkAcquired(item)}
                    className="border-zinc-700 text-emerald-400 hover:bg-emerald-500/10"
                    title={t('MARK_ACQUIRED')}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleDelete(item)}
                  className="border-zinc-700 text-red-400 hover:bg-blue-500/10"
                  title={t('DELETE_CAR')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-to-wishlist dialog
// ---------------------------------------------------------------------------

function AddWishlistDialog() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync: insert } = useInsertWishlist();
  const [open, setOpen] = useState(false);
  const [carName, setCarName] = useState('');
  const [brand, setBrand] = useState('');
  const [series, setSeries] = useState('');
  const [priority, setPriority] = useState<Priority>('Medium');
  const [notes, setNotes] = useState('');
  const [isPending, setIsPending] = useState(false);

  const handleAdd = async () => {
    if (!carName.trim()) {
      toast({ title: t('CAR_NAME'), description: 'Required', variant: 'destructive' });
      return;
    }
    setIsPending(true);
    try {
      await insert({
        CarName: carName.trim(),
        Brand: brand.trim() || undefined,
        Series: series.trim() || undefined,
        Priority: priority,
        Notes: notes.trim() || undefined,
      });
      toast({ title: t('WISHLIST_ADDED') });
      setCarName('');
      setBrand('');
      setSeries('');
      setPriority('Medium');
      setNotes('');
      setOpen(false);
    } catch (err) {
      toast({
        title: t('SAVE_FAILED'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#2563EB] hover:bg-blue-600 text-white font-semibold">
          <Plus className="h-4 w-4 mr-2" />
          {t('ADD_TO_WISHLIST')}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle>{t('ADD_TO_WISHLIST')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-zinc-300">{t('CAR_NAME')}</label>
            <Input
              value={carName}
              onChange={(e) => setCarName(e.target.value)}
              placeholder="Bone Shaker"
              className="bg-zinc-900 border-zinc-800"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-zinc-300">{t('BRAND')}</label>
              <Input
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="Hot Wheels"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-300">{t('SERIES')}</label>
              <Input
                value={series}
                onChange={(e) => setSeries(e.target.value)}
                placeholder="Mainline"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-zinc-300">{t('PRIORITY')}</label>
            <Select value={priority} onValueChange={(v) => setPriority(v as Priority)}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {t(`PRIORITY_${p.toUpperCase()}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-zinc-300">{t('NOTES')}</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-zinc-900 border-zinc-800"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
            {t('CANCEL')}
          </Button>
          <Button
            onClick={handleAdd}
            disabled={isPending}
            className="bg-[#2563EB] hover:bg-blue-600"
          >
            {isPending ? t('LOADING') : t('SAVE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}